import { selectActiveLoyaltyProvider } from "../loyalty/selectors";
import type { BillDiscount, DiscountOffer, Sale } from "./types";

/**
 * 🔴 **The ONLY file in `packages/core/discounts` that names a redux key.** Every rule module
 * takes its inputs as arguments and every hook reads through these, so a store reshape is a
 * change here and nowhere else — and the rules stay testable without a store.
 *
 * Plain selectors taking the whole state, so the same function serves `useSelector` in a
 * component and `store.getState()` in the adapter. Nothing is read at module-eval time (audit
 * checks H/J).
 */

/** The sale being discounted. */
export const selectSaleData = (state: any): Sale => state.saleDataStore?.saleData;

/**
 * A stable reference for "the catalog has no discounts".
 *
 * 🔴 Module-level, not a `?? {}` literal: web's `arrayToMap` stores `null` for an empty list, and
 * a fresh object per call makes `useSelector`'s reference compare report a change on EVERY store
 * action — re-rendering whatever reads this, the web cart among them since 2026-09-24
 * (`useCustomerRewards`). Same hazard, same fix, as `customerSummary/selectors`' `EMPTY`.
 */
const NO_CATALOG_DISCOUNTS: Record<string, DiscountOffer> = {};

/** The catalog's discounts, keyed by id. */
export const selectCatalogDiscounts = (state: any): Record<string, DiscountOffer> =>
  state.catalogStore?.discounts ?? NO_CATALOG_DISCOUNTS;

/**
 * The coupon API's last answer — the expanded discounts for a typed code, its `couponCode`, the
 * provider it attributed the code to, and a `requestId`.
 *
 * ⚠️ Read by `buildBillDiscount` and by both partner request builders. It is a SINGLE slot, not
 * a per-code map, so it describes whichever code was typed most recently.
 */
export const selectAppliedDiscountInfo = (state: any): Record<string, any> =>
  state.discountStore?.discountInfo ?? {};

/** Are the coupon API's discounts still in flight? */
export const selectIsDiscountApplying = (state: any): boolean =>
  !!state.discountStore?.isDiscountApplying;

/** Every approval row, for every sale. Filter with `getApprovalDataForSale`. */
export const selectDiscountApprovalData = (state: any): any[] =>
  state.discountStore?.discountApproval ?? [];

/**
 * The partner reference id from the current coupon conversation.
 *
 * ⚠️ Lives on the LOYALTY slice (`externalRefId`), because the partner endpoints it belongs to
 * are the loyalty ones. It is a single slot shared by every partner, which is why
 * `referenceIdSource` exists — writing one from a leg that does not issue it overwrites a good
 * id with `undefined`.
 */
export const selectExternalReferenceId = (state: any): string | undefined =>
  state.LoyaltyStore?.externalRefId;

/** Every configured loyalty/coupon provider. */
export const selectCouponProviders = (state: any): any[] =>
  state.businessStore?.businessData?.loyaltyProviders ?? [];

/**
 * The active provider, or `null`.
 *
 * Re-exported from `packages/core/loyalty/selectors` rather than re-derived: it filters by
 * branch and channel and prefers `isPrimary`, and two copies of that would drift. One definition.
 */
export const selectActiveCouponProvider = selectActiveLoyaltyProvider;

/** The merchant's business record — the count limits and the authentication switch live on it. */
export const selectBusinessData = (state: any): Record<string, any> =>
  state.businessStore?.businessData ?? {};

/** `accessPreferences` — the manager-approval thresholds. */
export const selectAccessPreferences = (state: any): Record<string, any> =>
  state.businessStore?.userData?.accessPreferences ?? {};

/** The merchant's configured discount reasons, for the notes prompt. */
export const selectDiscountReasons = (state: any): any[] =>
  state.businessStore?.businessData?.discountReasons ?? [];

export const selectIsOffline = (state: any): boolean => !!state.businessStore?.isOffline;

export const selectCustomerInfo = (state: any): Record<string, any> =>
  state.customerStore?.customerInfo ?? {};

export const selectCustomerSummary = (state: any): Record<string, any> =>
  state.customerStore?.customerSummary ?? {};

/**
 * The provider record whose `metaId` matches, for resolving a coupon back to its `mid`.
 *
 * ⚠️ The `mid` is what the partner APIs take as `providerId`, and the `metaId` is what a bill
 * discount records. A bill can carry a coupon from a partner that is no longer active, so this
 * lookup — not the active provider — is what the removal path must use.
 */
export const selectProviderByMetaId = (state: any, metaId?: string): any =>
  selectCouponProviders(state).find((provider: any) => provider?.metaId === metaId);

/** The partner offer on the bill, or `null`. */
export const selectExternalBillDiscount = (state: any): BillDiscount | null =>
  (selectSaleData(state) as any)?.discountList?.find((discount: any) => discount?.isExternal) ??
  null;
